import React, { ChangeEvent, useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TextField, Button, MenuItem, Select, SelectChangeEvent, CardMedia, IconButton, Input, Checkbox } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { 
  useGetBoxArtListQuery, 
  useGetFlavourListQuery, 
  useGetToppingListQuery,
  useUploadFileMutation,
  useCreateGreekBoxMutation,
  useUpdateGreekBoxMutation
} from "../../store/api/commonAPI";
import { showToast, showError } from "../../constants/toasts";
import Loader from "../../constants/Loader";

const AddGreekBox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get edit data from location state
  const editGreekBox = location.state as any;
  const isEditMode = !!editGreekBox;

  // State for form fields
  const [name, setName] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  
  // State for selects
  const [boxArt, setBoxArt] = React.useState<string[]>([]); // Changed to array for multi-select
  const [flavor, setFlavor] = React.useState<string[]>([]); // Changed to array for multi-select
  const [topping, setTopping] = React.useState<string[]>([]); // Changed to array for multi-select

  // Separate states for two image uploads
  const [image1, setImage1] = useState<string>("");
  const [imageFile1, setImageFile1] = useState<File | null>(null);
  const [uploadedImageUrl1, setUploadedImageUrl1] = useState<string>("");
  
  const [image2, setImage2] = useState<string>("");
  const [imageFile2, setImageFile2] = useState<File | null>(null);
  const [uploadedImageUrl2, setUploadedImageUrl2] = useState<string>("");

  // Form validation errors
  const [errors, setErrors] = useState<{
    name?: string;
    price?: string;
    description?: string;
    boxArt?: string;
    flavor?: string;
    topping?: string;
    image1?: string;
    image2?: string;
    flavorMin?: string;
    flavorMax?: string;
    toppingMin?: string;
    toppingMax?: string;
  }>({});

  // API mutations
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [createGreekBox, { isLoading: isCreating }] = useCreateGreekBoxMutation();
  const [updateGreekBox, { isLoading: isUpdating }] = useUpdateGreekBoxMutation();
  
  const isLoading = isUploading || isCreating || isUpdating;

  // Pre-populate form when in edit mode
  useEffect(() => {
    if (isEditMode && editGreekBox) {
      setName(editGreekBox.name || "");
      setPrice(editGreekBox.price?.toString() || "");
      setDescription(editGreekBox.description || editGreekBox.Description || "");
      
      // Handle box art - prioritize boxIds (plural), fallback to boxId (singular) or boxDetails array
      if (editGreekBox.boxIds && Array.isArray(editGreekBox.boxIds)) {
        setBoxArt(editGreekBox.boxIds);
      } else if (editGreekBox.boxId) {
        if (Array.isArray(editGreekBox.boxId)) {
          setBoxArt(editGreekBox.boxId);
        } else {
          setBoxArt([editGreekBox.boxId]);
        }
      } else if (editGreekBox.boxDetails && Array.isArray(editGreekBox.boxDetails)) {
        // Extract IDs from array of box detail objects
        const boxIds = editGreekBox.boxDetails
          .map((item: any) => item._id || item.id)
          .filter((id: any) => id);
        setBoxArt(boxIds);
      } else if (editGreekBox.boxDetails?._id) {
        // Fallback for single box detail object (legacy)
        setBoxArt([editGreekBox.boxDetails._id]);
      } else {
        setBoxArt([]);
      }
      
      // Handle flavors - prioritize flavorIds (plural), fallback to flavorId (singular) or flavourDetails array
      if (editGreekBox.flavorIds && Array.isArray(editGreekBox.flavorIds)) {
        setFlavor(editGreekBox.flavorIds);
      } else if (editGreekBox.flavorId) {
        if (Array.isArray(editGreekBox.flavorId)) {
          setFlavor(editGreekBox.flavorId);
        } else {
          setFlavor([editGreekBox.flavorId]);
        }
      } else if (editGreekBox.flavourDetails && Array.isArray(editGreekBox.flavourDetails)) {
        // Extract IDs from array of flavour detail objects
        const flavorIds = editGreekBox.flavourDetails
          .map((item: any) => item._id || item.id)
          .filter((id: any) => id);
        setFlavor(flavorIds);
      } else if (editGreekBox.flavourDetails?._id) {
        // Fallback for single flavour detail object (legacy)
        setFlavor([editGreekBox.flavourDetails._id]);
      } else {
        setFlavor([]);
      }
      
      // Handle toppings - prioritize toppingIds (plural), fallback to toppingId (singular) or toppingDetails array
      if (editGreekBox.toppingIds && Array.isArray(editGreekBox.toppingIds)) {
        setTopping(editGreekBox.toppingIds);
      } else if (editGreekBox.toppingId) {
        if (Array.isArray(editGreekBox.toppingId)) {
          setTopping(editGreekBox.toppingId);
        } else {
          setTopping([editGreekBox.toppingId]);
        }
      } else if (editGreekBox.toppingDetails && Array.isArray(editGreekBox.toppingDetails)) {
        // Extract IDs from array of topping detail objects
        const toppingIds = editGreekBox.toppingDetails
          .map((item: any) => item._id || item.id)
          .filter((id: any) => id);
        setTopping(toppingIds);
      } else if (editGreekBox.toppingDetails?._id) {
        // Fallback for single topping detail object (legacy)
        setTopping([editGreekBox.toppingDetails._id]);
      } else {
        setTopping([]);
      }
      
      setFlavorMin(editGreekBox.flavorMinCount || 0);
      setFlavorMax(editGreekBox.flavorMaxCount || 0);
      setToppingMin(editGreekBox.toppingMinCount || 0);
      setToppingMax(editGreekBox.toppingMaxCount || 0);
      
      // Set existing images
      if (editGreekBox.image) {
        setImage1(editGreekBox.image);
        setUploadedImageUrl1(editGreekBox.image);
      }
      if (editGreekBox.sliceImage) {
        setImage2(editGreekBox.sliceImage);
        setUploadedImageUrl2(editGreekBox.sliceImage);
      }
    }
  }, [isEditMode, editGreekBox]);

  // Fetch data for dropdowns
  const { data: boxArtData } = useGetBoxArtListQuery({ page: 1, limit: 1000 });
  const { data: flavorData } = useGetFlavourListQuery({ page: 1, limit: 1000 });
  const { data: toppingData } = useGetToppingListQuery({ page: 1, limit: 1000 });

  // Extract box art list
  const boxArtList = useMemo(() => {
    if (boxArtData?.data) {
      let boxArtArray: any[] = [];
      if (Array.isArray(boxArtData.data)) {
        boxArtArray = boxArtData.data;
      } else if (boxArtData.data.boxArt && Array.isArray(boxArtData.data.boxArt)) {
        boxArtArray = boxArtData.data.boxArt;
      } else if (boxArtData.data.boxarts && Array.isArray(boxArtData.data.boxarts)) {
        boxArtArray = boxArtData.data.boxarts;
      } else if (boxArtData.data.box && Array.isArray(boxArtData.data.box)) {
        boxArtArray = boxArtData.data.box;
      }
      return boxArtArray.map((item: any) => ({
        id: item._id || item.id,
        name: item.name || "N/A",
      }));
    }
    return [];
  }, [boxArtData]);

  // Extract flavor list
  const flavorList = useMemo(() => {
    if (flavorData?.data?.flavour && Array.isArray(flavorData.data.flavour)) {
      return flavorData.data.flavour.map((item: any) => ({
        id: item._id || item.id,
        name: item.name || "N/A",
      }));
    }
    return [];
  }, [flavorData]);

  // Extract topping list
  const toppingList = useMemo(() => {
    if (toppingData?.data?.topping && Array.isArray(toppingData.data.topping)) {
      return toppingData.data.topping.map((item: any) => ({
        id: item._id || item.id,
        name: item.name || "N/A",
      }));
    }
    return [];
  }, [toppingData]);

  // State for inputs
  const [flavorMin, setFlavorMin] = useState<number>(0);
  const [flavorMax, setFlavorMax] = useState<number>(0);

  const [toppingMin, setToppingMin] = useState<number>(0);
  const [toppingMax, setToppingMax] = useState<number>(0);

  // Handlers for selects
  const handleBoxArtChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setBoxArt(typeof value === 'string' ? value.split(',') : value);
    if (errors.boxArt) {
      setErrors({ ...errors, boxArt: undefined });
    }
  };

  const handleFlavorChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFlavor(typeof value === 'string' ? value.split(',') : value);
    if (errors.flavor) {
      setErrors({ ...errors, flavor: undefined });
    }
  };

  const handleToppingChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setTopping(typeof value === 'string' ? value.split(',') : value);
    if (errors.topping) {
      setErrors({ ...errors, topping: undefined });
    }
  };

  // Image upload handlers
  const handleImage1Change = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files[0]?.type.includes("image")) {
      setImageFile1(files[0]);
      setImage1(URL.createObjectURL(files[0]));
      if (errors.image1) {
        setErrors({ ...errors, image1: undefined });
      }
    }
  };

  const handleImage2Change = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files[0]?.type.includes("image")) {
      setImageFile2(files[0]);
      setImage2(URL.createObjectURL(files[0]));
      if (errors.image2) {
        setErrors({ ...errors, image2: undefined });
      }
    }
  };

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!price.trim()) {
      newErrors.price = "Price is required";
    } else {
      const priceNum = parseFloat(price);
      if (isNaN(priceNum) || priceNum <= 0) {
        newErrors.price = "Price must be a positive number";
      }
    }

    if (!description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!boxArt || boxArt.length === 0) {
      newErrors.boxArt = "At least one Box Art is required";
    }

    if (!flavor || flavor.length === 0) {
      newErrors.flavor = "At least one Flavor is required";
    }

    if (!topping || topping.length === 0) {
      newErrors.topping = "At least one Topping is required";
    }

    // Require images - in edit mode, existing images (uploadedImageUrl) are acceptable
    if (!image1 && !uploadedImageUrl1) {
      newErrors.image1 = "Image is required";
    }

    if (!image2 && !uploadedImageUrl2) {
      newErrors.image2 = "Slice Image is required";
    }

    if (flavorMin < 0) {
      newErrors.flavorMin = "Flavor Min Count must be 0 or greater";
    }

    if (flavorMax < flavorMin) {
      newErrors.flavorMax = "Flavor Max Count must be greater than or equal to Min Count";
    }

    if (toppingMin < 0) {
      newErrors.toppingMin = "Topping Min Count must be 0 or greater";
    }

    if (toppingMax < toppingMin) {
      newErrors.toppingMax = "Topping Max Count must be greater than or equal to Min Count";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showError("Please fill all required fields correctly");
      return;
    }

    try {
      let imageUrl = uploadedImageUrl1;
      let sliceImageUrl = uploadedImageUrl2;

      // Step 1: Upload first image if a new file is selected
      if (imageFile1) {
        const uploadResult = await uploadFile({ file: imageFile1 }).unwrap();
        imageUrl = uploadResult.data?.data || uploadResult.data;
        
        if (!imageUrl) {
          showError("Failed to upload image. Please try again.");
          return;
        }
      }

      // Step 2: Upload second image (slice image) if a new file is selected
      if (imageFile2) {
        const sliceUploadResult = await uploadFile({ file: imageFile2 }).unwrap();
        sliceImageUrl = sliceUploadResult.data?.data || sliceUploadResult.data;
        
        if (!sliceImageUrl) {
          showError("Failed to upload slice image. Please try again.");
          return;
        }
      }

      // Step 3: Create or Update Greek Box
      let response;
      const greekBoxId = editGreekBox?.id || editGreekBox?._id;
      console.log("Edit Mode:", isEditMode, "Greek Box ID:", greekBoxId, "Edit Data:", editGreekBox);
      
      if (isEditMode && greekBoxId) {
        // Build update payload with only changed fields (id goes in URL, not in body)
        const updatePayload: any = {};

        // Only include fields that have actually changed
        const originalName = editGreekBox.name || "";
        if (name.trim() !== originalName) {
          updatePayload.name = name.trim();
        }
        
        const originalImage = editGreekBox.image || "";
        if (imageUrl !== originalImage) {
          updatePayload.image = imageUrl;
        }
        
        const originalSliceImage = editGreekBox.sliceImage || "";
        if (sliceImageUrl !== originalSliceImage) {
          updatePayload.sliceImage = sliceImageUrl;
        }
        
        const originalPrice = editGreekBox.price || 0;
        const newPrice = parseFloat(price);
        if (newPrice !== originalPrice) {
          updatePayload.price = newPrice;
        }
        
        const originalDescription = editGreekBox.description || editGreekBox.Description || "";
        if (description.trim() !== originalDescription) {
          updatePayload.description = description.trim();
        }
        
        // Get original box art IDs - prioritize boxIds (plural), fallback to boxId (singular) or boxDetails array
        let originalBoxArtArray: string[] = [];
        if (editGreekBox.boxIds && Array.isArray(editGreekBox.boxIds)) {
          originalBoxArtArray = editGreekBox.boxIds;
        } else if (editGreekBox.boxId) {
          originalBoxArtArray = Array.isArray(editGreekBox.boxId) ? editGreekBox.boxId : [editGreekBox.boxId];
        } else if (editGreekBox.boxDetails && Array.isArray(editGreekBox.boxDetails)) {
          // Extract IDs from array of box detail objects
          originalBoxArtArray = editGreekBox.boxDetails
            .map((item: any) => item._id || item.id)
            .filter((id: any) => id);
        } else if (editGreekBox.boxDetails?._id) {
          // Fallback for single box detail object (legacy)
          originalBoxArtArray = [editGreekBox.boxDetails._id];
        }
        // Check if box art has changed
        if (JSON.stringify([...boxArt].sort()) !== JSON.stringify([...originalBoxArtArray].sort())) {
          updatePayload.boxIds = boxArt;
        }
        
        // Get original flavor IDs - prioritize flavorIds (plural), fallback to flavorId (singular) or flavourDetails array
        let originalFlavorArray: string[] = [];
        if (editGreekBox.flavorIds && Array.isArray(editGreekBox.flavorIds)) {
          originalFlavorArray = editGreekBox.flavorIds;
        } else if (editGreekBox.flavorId) {
          originalFlavorArray = Array.isArray(editGreekBox.flavorId) ? editGreekBox.flavorId : [editGreekBox.flavorId];
        } else if (editGreekBox.flavourDetails && Array.isArray(editGreekBox.flavourDetails)) {
          // Extract IDs from array of flavour detail objects
          originalFlavorArray = editGreekBox.flavourDetails
            .map((item: any) => item._id || item.id)
            .filter((id: any) => id);
        } else if (editGreekBox.flavourDetails?._id) {
          // Fallback for single flavour detail object (legacy)
          originalFlavorArray = [editGreekBox.flavourDetails._id];
        }
        // Check if flavors have changed
        if (JSON.stringify([...flavor].sort()) !== JSON.stringify([...originalFlavorArray].sort())) {
          updatePayload.flavorIds = flavor;
        }
        
        const originalFlavorMin = editGreekBox.flavorMinCount || 0;
        if (flavorMin !== originalFlavorMin) {
          updatePayload.flavorMinCount = flavorMin;
        }
        
        const originalFlavorMax = editGreekBox.flavorMaxCount || 0;
        if (flavorMax !== originalFlavorMax) {
          updatePayload.flavorMaxCount = flavorMax;
        }
        
        // Get original topping IDs - prioritize toppingIds (plural), fallback to toppingId (singular) or toppingDetails array
        let originalToppingArray: string[] = [];
        if (editGreekBox.toppingIds && Array.isArray(editGreekBox.toppingIds)) {
          originalToppingArray = editGreekBox.toppingIds;
        } else if (editGreekBox.toppingId) {
          originalToppingArray = Array.isArray(editGreekBox.toppingId) ? editGreekBox.toppingId : [editGreekBox.toppingId];
        } else if (editGreekBox.toppingDetails && Array.isArray(editGreekBox.toppingDetails)) {
          // Extract IDs from array of topping detail objects
          originalToppingArray = editGreekBox.toppingDetails
            .map((item: any) => item._id || item.id)
            .filter((id: any) => id);
        } else if (editGreekBox.toppingDetails?._id) {
          // Fallback for single topping detail object (legacy)
          originalToppingArray = [editGreekBox.toppingDetails._id];
        }
        // Check if toppings have changed
        if (JSON.stringify([...topping].sort()) !== JSON.stringify([...originalToppingArray].sort())) {
          updatePayload.toppingIds = topping;
        }
        
        const originalToppingMin = editGreekBox.toppingMinCount || 0;
        if (toppingMin !== originalToppingMin) {
          updatePayload.toppingMinCount = toppingMin;
        }
        
        const originalToppingMax = editGreekBox.toppingMaxCount || 0;
        if (toppingMax !== originalToppingMax) {
          updatePayload.toppingMaxCount = toppingMax;
        }

        // Only include isActive and featured if they were actually changed by user
        // (These would need to be tracked separately if you have UI controls for them)

        console.log("Update Payload (only changed fields):", updatePayload);
        
        // Update existing Greek Box (id is passed separately for URL, not in body)
        response = await updateGreekBox({
          id: greekBoxId,
          ...updatePayload,
        }).unwrap();

        // Show success message
        const successMessage = response?.message || "Greek Box updated successfully";
        showToast(successMessage);
      } else {
        // Create new Greek Box
        response = await createGreekBox({
          name: name.trim(),
          image: imageUrl,
          sliceImage: sliceImageUrl,
          price: parseFloat(price),
          description: description.trim(),
          boxIds: boxArt,
          flavorIds: flavor,
          flavorMinCount: flavorMin,
          flavorMaxCount: flavorMax,
          toppingIds: topping,
          toppingMinCount: toppingMin,
          toppingMaxCount: toppingMax,
        }).unwrap();

        // Show success message
        const successMessage = response?.message || "Greek Box created successfully";
        showToast(successMessage);
      }

      // Navigate back to list page
      navigate("/manage-greek-box");
    } catch (error: any) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} Greek box:`, error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        `Failed to ${isEditMode ? "update" : "create"} Greek box. Please try again.`;
      showError(errorMessage);
    }
  };

  return (
    <>
      <Loader isLoad={isLoading} />
      <div className="main_title">
        <h1>{isEditMode ? "Edit Greek Box" : "Add Greek Box"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button>
          {" "} - <button onClick={() => navigate("/manage-greek-box")}>Manage Greek Box</button>
          {" "} - {isEditMode ? "Edit Greek Box" : "Add Greek Box"}
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={handleSubmit}>
          <div className="gap_p">
            <div className="control_group">
              <label htmlFor="icon-button-file">Upload Image</label>
              {(errors.image1 || errors.image2) && (
                <p style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "0 0 8px 0" }}>
                  {errors.image1 || errors.image2}
                </p>
              )}
              <div className="image_grid">
                {/* First Image Upload */}
                {image1 ? (
                  <div className="upload_image">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia
                          component="img"
                          image={image1}
                          alt="Uploaded image 1"
                        />
                      </figure>
                      <IconButton
                        aria-label="Remove uploaded image"
                        onClick={() => {
                          setImage1("");
                          setImageFile1(null);
                          setUploadedImageUrl1("");
                        }}
                        size="small"
                        disabled={isLoading}
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                  </div>
                ) : (
                  <label
                    className="upload_image no_radius"
                    htmlFor="icon-button-file-1"
                  >
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file-1"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={handleImage1Change}
                      disabled={isLoading}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/placeholder.png"
                          alt="placeholder"
                        />
                        <span className="upload_img_icon">
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="Upload"
                          />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}

                {/* Second Image Upload */}
                {image2 ? (
                  <div className="upload_image">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia
                          component="img"
                          image={image2}
                          alt="Uploaded image 2"
                        />
                      </figure>
                      <IconButton
                        aria-label="Remove uploaded image"
                        onClick={() => {
                          setImage2("");
                          setImageFile2(null);
                          setUploadedImageUrl2("");
                        }}
                        size="small"
                        disabled={isLoading}
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                  </div>
                ) : (
                  <label
                    className="upload_image no_radius"
                    htmlFor="icon-button-file-2"
                  >
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file-2"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={handleImage2Change}
                      disabled={isLoading}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/placeholder.png"
                          alt="placeholder"
                        />
                        <span className="upload_img_icon">
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="Upload"
                          />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
              </div>
            </div>


            <div className="control_group w_66">
              <label htmlFor="name">Name *</label>
              <TextField 
                id="name" 
                placeholder="Enter Name" 
                hiddenLabel 
                fullWidth 
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) {
                    setErrors({ ...errors, name: undefined });
                  }
                }}
                error={!!errors.name}
                helperText={errors.name}
                disabled={isLoading}
              />
            </div>

            <div className="control_group w_33">
              <label htmlFor="price">Price *</label>
              <TextField 
                id="price" 
                placeholder="Enter Price" 
                hiddenLabel 
                fullWidth 
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  if (errors.price) {
                    setErrors({ ...errors, price: undefined });
                  }
                }}
                error={!!errors.price}
                helperText={errors.price}
                disabled={isLoading}
              />
            </div>

            <div className="control_group w_100">
              <label htmlFor="description">Description *</label>
              <TextField
                id="description"
                placeholder="Enter Description"
                hiddenLabel
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    setErrors({ ...errors, description: undefined });
                  }
                }}
                error={!!errors.description}
                helperText={errors.description}
                disabled={isLoading}
              />
            </div>

            <div className="control_group w_100">
              <label htmlFor="boxArt">Box Art *</label>
              <Select
                id="boxArt"
                fullWidth
                multiple
                value={boxArt}
                onChange={handleBoxArtChange}
                displayEmpty
                error={!!errors.boxArt}
                disabled={isLoading}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em style={{ color: '#999' }}>Select Art Box</em>;
                  }
                  return selected
                    .map((id) => boxArtList.find((b) => b.id === id)?.name)
                    .filter(Boolean)
                    .join(", ");
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                <MenuItem disabled value="">
                  <em>Select Art Box</em>
                </MenuItem>
                {boxArtList.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Checkbox checked={boxArt.indexOf(item.id) > -1} />
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.boxArt && (
                <p style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>
                  {errors.boxArt}
                </p>
              )}
            </div>

            <div className="control_group w_66">
              <label htmlFor="flavors">Flavors *</label>
              <Select
                id="flavors"
                fullWidth
                multiple
                value={flavor}
                onChange={handleFlavorChange}
                displayEmpty
                error={!!errors.flavor}
                disabled={isLoading}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em style={{ color: '#999' }}>Select Flavours</em>;
                  }
                  return selected
                    .map((id) => flavorList.find((f) => f.id === id)?.name)
                    .filter(Boolean)
                    .join(", ");
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                <MenuItem disabled value="">
                  <em>Select Flavours</em>
                </MenuItem>
                {flavorList.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Checkbox checked={flavor.indexOf(item.id) > -1} />
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.flavor && (
                <p style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>
                  {errors.flavor}
                </p>
              )}
            </div>

            <div className="control_group w_33">
              <div className="gap_p">
                <div className="control_group w_50">
                  <label htmlFor="flavorMin">Flavors Min Count</label>
                  <TextField
                    id="flavorMin"
                    type="number"
                    placeholder="Min"
                    hiddenLabel
                    fullWidth
                    inputProps={{ min: 0 }}
                    value={flavorMin}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setFlavorMin(value >= 0 ? value : 0);
                      if (errors.flavorMin) {
                        setErrors({ ...errors, flavorMin: undefined });
                      }
                    }}
                    error={!!errors.flavorMin}
                    helperText={errors.flavorMin}
                    disabled={isLoading}
                  />
                </div>

                <div className="control_group w_50">
                  <label htmlFor="flavorMax">Flavors Max Count</label>
                  <TextField
                    id="flavorMax"
                    type="number"
                    placeholder="Max"
                    hiddenLabel
                    fullWidth
                    inputProps={{ min: 0 }}
                    value={flavorMax}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value >= flavorMin) {
                        setFlavorMax(value);
                        if (errors.flavorMax) {
                          setErrors({ ...errors, flavorMax: undefined });
                        }
                      }
                    }}
                    error={!!errors.flavorMax}
                    helperText={errors.flavorMax}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="control_group w_66">
              <label htmlFor="toppings">Toppings *</label>
              <Select
                id="toppings"
                fullWidth
                multiple
                value={topping}
                onChange={handleToppingChange}
                displayEmpty
                error={!!errors.topping}
                disabled={isLoading}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em style={{ color: '#999' }}>Select Toppings</em>;
                  }
                  return selected
                    .map((id) => toppingList.find((t) => t.id === id)?.name)
                    .filter(Boolean)
                    .join(", ");
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                <MenuItem disabled value="">
                  <em>Select Toppings</em>
                </MenuItem>
                {toppingList.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <Checkbox checked={topping.indexOf(item.id) > -1} />
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.topping && (
                <p style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>
                  {errors.topping}
                </p>
              )}
            </div>

            <div className="control_group w_33">
              <div className="gap_p">
                <div className="control_group w_50">
                  <label htmlFor="toppingMin">Toppings Min Count</label>
                  <TextField
                    id="toppingMin"
                    type="number"
                    placeholder="Min"
                    hiddenLabel
                    fullWidth
                    inputProps={{ min: 0 }}
                    value={toppingMin}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setToppingMin(value >= 0 ? value : 0);
                      if (errors.toppingMin) {
                        setErrors({ ...errors, toppingMin: undefined });
                      }
                    }}
                    error={!!errors.toppingMin}
                    helperText={errors.toppingMin}
                    disabled={isLoading}
                  />
                </div>

                <div className="control_group w_50">
                  <label htmlFor="toppingMax">Toppings Max Count</label>
                  <TextField
                    id="toppingMax"
                    type="number"
                    placeholder="Max"
                    hiddenLabel
                    fullWidth
                    inputProps={{ min: 0 }}
                    value={toppingMax}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value >= toppingMin) {
                        setToppingMax(value);
                        if (errors.toppingMax) {
                          setErrors({ ...errors, toppingMax: undefined });
                        }
                      }
                    }}
                    error={!!errors.toppingMax}
                    helperText={errors.toppingMax}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

          </div>

          <div className="form_btn">
            <Button 
              type="submit" 
              className="btn btn_primary"
              disabled={isLoading}
            >
              {isLoading ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update" : "Save")}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddGreekBox;
